import React, { useMemo, useState } from "react";
import {
    SectionedCheckboxTable,
    type Section,
} from "@design-system/SectionedCheckboxTable/SectionedCheckboxTable";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { BooleanControl, NumberControl } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./DatePickerPlayground.module.scss";
import gridStyles from "./playgroundControls.module.scss";

const accountsBySection = [
    {
        title: "Cash",
        accounts: [
            { value: "checking", label: "Checking account" },
            { value: "savings", label: "Savings account" },
        ],
    },
    {
        title: "Credit",
        accounts: [
            { value: "credit-card", label: "Credit card" },
            { value: "line-of-credit", label: "Line of credit" },
        ],
    },
];

const allAccountValues = accountsBySection.flatMap((section) =>
    section.accounts.map((account) => account.value),
);

const unavailableAccountValue = "line-of-credit";

const selectionPresets = ["none", "some", "all"] as const;

type SelectionPreset = (typeof selectionPresets)[number];

const selectionPresetValues: Record<SelectionPreset, string[]> = {
    none: [],
    some: [allAccountValues[0]],
    all: allAccountValues,
};

const selectionPresetForValue = (value: string[]): SelectionPreset => {
    if (value.length === 0) {
        return "none";
    }

    return value.length === allAccountValues.length ? "all" : "some";
};

const clampHeight = (value: number) =>
    Number.isFinite(value) ? Math.min(Math.max(value, 80), 600) : undefined;

const renderLabel = (label: string) => () => label;

const buildSections = ({
    allowSelectAll,
    unavailableValues,
}: {
    allowSelectAll: boolean;
    unavailableValues: string[];
}): Array<Section<string>> =>
    accountsBySection.map((section) => ({
        title: section.title,
        allowSelectAll,
        items: section.accounts.map((account) => {
            const isUnavailable = unavailableValues.includes(account.value);

            return {
                value: account.value,
                searchValue: account.label,
                disabled: isUnavailable,
                render: renderLabel(
                    isUnavailable
                        ? `${account.label} (unavailable)`
                        : account.label,
                ),
            };
        }),
    }));

export const SectionedCheckboxTablePlayground = () => {
    const [value, setValue] = useState<string[]>(["checking"]);
    const [allowSelectAll, setAllowSelectAll] = useState(true);
    const [isSearchable, setIsSearchable] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [hasUnavailableRow, setHasUnavailableRow] = useState(false);
    const [autoHeightMax, setAutoHeightMax] = useState(320);

    const selectionPreset = selectionPresetForValue(value);

    const sections = useMemo(
        () =>
            buildSections({
                allowSelectAll,
                unavailableValues: hasUnavailableRow
                    ? [unavailableAccountValue]
                    : [],
            }),
        [allowSelectAll, hasUnavailableRow],
    );

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Features</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Allow select all per section"
                                checked={allowSelectAll}
                                onChange={setAllowSelectAll}
                            />
                            <BooleanControl
                                label="Searchable"
                                checked={isSearchable}
                                onChange={setIsSearchable}
                            />
                            <BooleanControl
                                label="Disabled"
                                checked={isDisabled}
                                onChange={setIsDisabled}
                            />
                            <BooleanControl
                                label="Disable one row"
                                checked={hasUnavailableRow}
                                onChange={setHasUnavailableRow}
                            />
                        </div>
                    </section>
                    <section>
                        <h2>Scroll area</h2>
                        <NumberControl
                            label="Max height (px)"
                            min={80}
                            max={600}
                            step={20}
                            value={autoHeightMax}
                            onChange={(nextValue) => {
                                const nextHeight = clampHeight(nextValue);
                                if (nextHeight !== undefined) {
                                    setAutoHeightMax(nextHeight);
                                }
                            }}
                        />
                    </section>
                    <section>
                        <h2>Selection</h2>
                        <ToggleGroup.Root<SelectionPreset>
                            value={selectionPreset}
                            onValueChange={(nextPreset) => {
                                if (nextPreset) {
                                    setValue(selectionPresetValues[nextPreset]);
                                }
                            }}
                        >
                            {selectionPresets.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={`${value.length} of ${allAccountValues.length} selected · shift-click for ranges`}
                width="wide"
                output={JSON.stringify(
                    {
                        SectionedCheckboxTable: {
                            value,
                            disabled: isDisabled,
                            searchable: isSearchable,
                            autoHeightMax,
                        },
                        sectionProps: {
                            allowSelectAll,
                            disabledItems: hasUnavailableRow
                                ? [unavailableAccountValue]
                                : [],
                        },
                        showcaseState: {
                            selectionPreset,
                            sectionTitles: accountsBySection.map(
                                (section) => section.title,
                            ),
                        },
                    },
                    null,
                    2,
                )}
            >
                <SectionedCheckboxTable
                    className={gridStyles.fillWidth}
                    sections={sections}
                    value={value}
                    onChange={setValue}
                    disabled={isDisabled}
                    searchable={isSearchable}
                    autoHeightMax={autoHeightMax}
                />
            </ShowcasePreview>
        </SandboxLayout>
    );
};
